/**
 * 저장된 카드 관리 훅
 * @CODE:API-302 | SPEC: olulo-mx-admin/.claude/specs/SPEC-302-payment-advanced-features.md
 *
 * 저장된 카드 목록 조회, 등록, 수정, 삭제, 기본 카드 설정 기능을 제공합니다.
 */
import { useState, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { savedCardsApi } from '@/lib/api/savedCards';
import { ApiClientError } from '@/lib/api/client';
import type {
  BloominCardRegistrationPayload,
  CardRegistrationSession,
  SavedCard,
} from '@/lib/api/types';
import { logger } from '@/utils/logger';

const toSafePaymentErrorLog = (err: unknown) => {
  if (err instanceof ApiClientError) {
    return {
      name: err.name,
      status: err.status,
      code: err.code,
      isServerMessage: err.isServerMessage,
    };
  }

  if (err instanceof Error) {
    return { name: err.name };
  }

  return { type: typeof err };
};

const staleOperation = () =>
  new DOMException('Saved card operation is no longer current', 'AbortError');

export interface UseSavedCardsOptions {
  /**
   * 컴포넌트 마운트 시 자동으로 카드 목록 조회
   * @default true
   */
  autoFetch?: boolean;
}

export interface UseSavedCardsReturn {
  /**
   * 저장된 카드 목록
   */
  cards: SavedCard[];

  /**
   * 기본 카드 (is_default=true인 카드)
   */
  defaultCard: SavedCard | null;

  /**
   * 사용 가능한 카드 목록 (is_usable=true인 카드)
   */
  usableCards: SavedCard[];

  /**
   * 카드 목록 조회
   */
  fetchCards: () => Promise<void>;

  /**
   * 카드 등록 체크아웃 생성
   * @returns Bloomin 카드 입력 세션
   */
  createCheckout: () => Promise<CardRegistrationSession>;

  /**
   * Bloomin 카드 등록 확인
   * @param cardPayload - Bloomin 카드 토큰화 요청 필드
   * @param nickname - 선택적 카드 별명
   */
  confirmBloominRegistration: (
    cardPayload: BloominCardRegistrationPayload,
    nickname?: string
  ) => Promise<SavedCard>;

  /**
   * 카드 별명 업데이트
   * @param cardId - 카드 ID
   * @param nickname - 새 별명 (null로 설정하면 별명 삭제)
   */
  updateNickname: (cardId: number, nickname: string | null) => Promise<SavedCard>;

  /**
   * 기본 카드 설정
   * @param cardId - 카드 ID
   */
  setDefault: (cardId: number) => Promise<SavedCard>;

  /**
   * 카드 삭제
   * @param cardId - 카드 ID
   */
  deleteCard: (cardId: number) => Promise<void>;

  /**
   * 로딩 상태
   */
  isLoading: boolean;

  /**
   * 작업 진행 상태 (등록, 삭제 등 mutation 작업)
   */
  isMutating: boolean;

  /**
   * 에러 메시지
   */
  error: string | null;

  /**
   * 에러 초기화
   */
  clearError: () => void;

  /**
   * 카드 목록 새로고침
   */
  refresh: () => Promise<void>;

  /**
   * 현재 체크아웃 정보 (카드 등록 시 사용)
   */
  checkout: CardRegistrationSession | null;

  /**
   * 체크아웃 생성 중 여부
   */
  isCreatingCheckout: boolean;

  /**
   * 카드 등록 확인 중 여부
   */
  isConfirming: boolean;
}

export function useSavedCards(
  options: UseSavedCardsOptions = {}
): UseSavedCardsReturn {
  const { autoFetch = true } = options;
  const { isLoggedIn, user, customerId } = useAuth();
  const scope = useMemo(() => ({
    isLoggedIn,
    userId: user?.id,
    customerId,
    active: false,
    lifecycle: 0,
    readSequence: 0,
    readController: null as AbortController | null,
    pending: 0,
    creating: 0,
    confirming: 0,
    reconcile: false,
    mutationError: null as string | null,
  }), [isLoggedIn, user?.id, customerId]);
  const ownerRef = useRef(scope);
  const [cardsState, setCards] = useState<SavedCard[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [checkout, setCheckout] = useState<CardRegistrationSession | null>(null);
  const [isCreatingCheckout, setIsCreatingCheckout] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  useLayoutEffect(() => {
    ownerRef.current = scope;
    scope.active = true;
    scope.lifecycle += 1;
    setCards([]);
    setCheckout(null);
    setError(null);
    setIsLoading(false);
    setIsMutating(false);
    setIsCreatingCheckout(false);
    setIsConfirming(false);
    return () => {
      scope.active = false;
      scope.lifecycle += 1;
      scope.readSequence += 1;
      scope.readController?.abort();
    };
  }, [scope]);

  const isCurrent = useCallback(() =>
    scope.isLoggedIn && (scope.userId != null || scope.customerId != null) &&
    ownerRef.current === scope && scope.active,
  [scope]);

  const readCards = useCallback(async (preserveError = false) => {
    if (!isCurrent()) return;
    if (scope.pending > 0) {
      scope.reconcile = true;
      return;
    }
    scope.readController?.abort();
    const controller = new AbortController();
    scope.readController = controller;
    const sequence = ++scope.readSequence;
    const active = () => isCurrent() && sequence === scope.readSequence && !controller.signal.aborted;
    setIsLoading(true);
    if (!preserveError) setError(null);
    try {
      const response = await savedCardsApi.getCards(controller.signal);
      if (active()) setCards(response.data);
    } catch (err) {
      if (!active()) return;
      if (!preserveError) setError(err instanceof Error ? err.message : '카드 목록을 불러오지 못했습니다');
      logger.error('[useSavedCards] fetchCards error:', err);
    } finally {
      if (active()) setIsLoading(false);
      if (scope.readController === controller) scope.readController = null;
    }
  }, [isCurrent, scope]);

  const fetchCards = useCallback(() => readCards(), [readCards]);

  // Writes are sent once. An obsolete result must not escape to a caller that
  // could open its checkout or select its card under a different account.
  const mutate = useCallback(async <T,>(
    write: () => Promise<T>,
    apply: (value: T) => void,
    fallback: string,
    kind: 'checkout' | 'registration' | 'card',
    reconcile = false,
  ): Promise<T> => {
    if (!isCurrent()) throw staleOperation();
    const lifecycle = scope.lifecycle;
    const active = () => isCurrent() && scope.lifecycle === lifecycle;
    if (scope.readController || reconcile) scope.reconcile = true;
    scope.readSequence += 1;
    scope.readController?.abort();
    scope.readController = null;
    if (scope.pending === 0) scope.mutationError = null;
    scope.pending += 1;
    if (kind === 'checkout') scope.creating += 1;
    if (kind === 'registration') scope.confirming += 1;
    setIsLoading(false);
    setIsMutating(true);
    setIsCreatingCheckout(scope.creating > 0);
    setIsConfirming(scope.confirming > 0);
    setError(scope.mutationError);
    let value: T;
    try {
      value = await write();
      if (!active()) throw staleOperation();
      apply(value);
    } catch (err) {
      if (!active()) throw staleOperation();
      if (kind === 'registration') {
        logger.error('Bloomin card registration failed:', toSafePaymentErrorLog(err));
        scope.mutationError = fallback;
        setError(fallback);
        throw new Error(fallback);
      }
      scope.mutationError = err instanceof Error ? err.message : fallback;
      setError(scope.mutationError);
      throw err;
    } finally {
      scope.pending -= 1;
      if (kind === 'checkout') scope.creating -= 1;
      if (kind === 'registration') scope.confirming -= 1;
      if (active()) {
        setIsMutating(scope.pending > 0);
        setIsCreatingCheckout(scope.creating > 0);
        setIsConfirming(scope.confirming > 0);
        if (scope.pending === 0 && scope.reconcile) {
          scope.reconcile = false;
          await readCards(scope.mutationError !== null);
        }
      }
    }
    if (!active()) throw staleOperation();
    return value;
  }, [isCurrent, scope, readCards]);

  const createCheckout = useCallback(() => mutate(async () => {
    const response = await savedCardsApi.createCheckout();
    const gateway = response.data.gateway ?? 'bloomin';
    return {
      gateway,
      checkoutId: response.data.checkout_id,
      requiresCardInput: response.data.requires_card_input ?? gateway === 'bloomin',
    };
  }, setCheckout, '카드 등록을 시작할 수 없습니다', 'checkout'), [mutate]);

  const confirmBloominRegistration = useCallback((
    cardPayload: BloominCardRegistrationPayload, nickname?: string,
  ) => mutate(async () => (await savedCardsApi.confirmRegistration({ ...cardPayload, nickname })).data, savedCard => {
    setCards(previous => [savedCard, ...previous.filter(card => card.id !== savedCard.id)
      .map(card => savedCard.is_default ? { ...card, is_default: false } : card)]);
    setCheckout(null);
  }, '카드 등록에 실패했습니다', 'registration'), [mutate]);

  const updateNickname = useCallback((cardId: number, nickname: string | null) =>
    mutate(async () => (await savedCardsApi.updateNickname(cardId, { nickname })).data, savedCard => {
      setCards(previous => previous.map(card => card.id === cardId ? savedCard : card));
    }, '카드 별명 수정에 실패했습니다', 'card'), [mutate]);

  const setDefault = useCallback((cardId: number) =>
    mutate(async () => (await savedCardsApi.setDefault(cardId)).data, () => {},
      '기본 카드 설정에 실패했습니다', 'card', true), [mutate]);

  const deleteCard = useCallback((cardId: number) =>
    mutate(async () => { await savedCardsApi.deleteCard(cardId); }, () => {},
      '카드 삭제에 실패했습니다', 'card', true), [mutate]);

  const clearError = useCallback(() => {
    if (isCurrent()) setError(null);
  }, [isCurrent]);

  useEffect(() => {
    if (autoFetch) void fetchCards();
  }, [autoFetch, fetchCards]);

  const owned = isLoggedIn && ownerRef.current === scope;
  const cards = owned ? cardsState : [];
  return {
    cards,
    defaultCard: cards.find(card => card.is_default) ?? null,
    usableCards: cards.filter(card => card.is_usable),
    fetchCards,
    createCheckout,
    confirmBloominRegistration,
    updateNickname,
    setDefault,
    deleteCard,
    isLoading: owned && isLoading,
    isMutating: owned && isMutating,
    error: owned ? error : null,
    clearError,
    refresh: fetchCards,
    checkout: owned ? checkout : null,
    isCreatingCheckout: owned && isCreatingCheckout,
    isConfirming: owned && isConfirming,
  };
}

export default useSavedCards;
